/**
 * Admin impersonation helpers (LIFF side).
 *
 * When the URL contains `?_imp=<jwt>`, LIFF skips LINE login and acts as
 * the customer named in the JWT. All API calls carry
 * `Authorization: AdminImp <jwt>` instead of `Bearer <lineIdToken>`.
 */

const STORAGE_KEY = 'meso_admin_impersonation';

export interface ImpersonationSession {
  token: string;
  storedAt: number;
}

/** Called at app start — if URL has `?_imp=xxx`, save it and strip from URL */
export function captureImpersonationFromUrl(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('_imp');
    if (!t) return false;
    const session: ImpersonationSession = { token: t, storedAt: Date.now() };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    // Strip _imp from URL so it doesn't get reused / leaked in history
    params.delete('_imp');
    const cleaned = window.location.pathname +
                    (params.toString() ? `?${params}` : '') +
                    window.location.hash;
    window.history.replaceState({}, '', cleaned);
    return true;
  } catch { return false; }
}

export function getImpersonationToken(): string | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as ImpersonationSession;
    return s?.token ?? null;
  } catch { return null; }
}

export function isImpersonating(): boolean {
  return getImpersonationToken() !== null;
}

export function endImpersonation(): void {
  sessionStorage.removeItem(STORAGE_KEY);
  // Reload to reset all state
  window.location.href = '/';
}
